import qs from 'querystring';
import baseAPI from '../app/axios';

export default class AiBotService {
  static async getData(orgId, id) {
    if (!orgId) {
      throw new Error('AiBotService.getData: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getData: id undefined');
    }

    const response = await baseAPI.get(`/ai-bot/${id}?orgId=${orgId}`);

    return response || {};
  }

  static async updateData(orgId, id, data) {
    if (!orgId) {
      throw new Error('AiBotService.updateData: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.updateData: id undefined');
    }

    const response = await baseAPI.put(`/ai-bot/${id}`, {
      ...data,
      orgId,
    });

    return response || {};
  }

  static async publishData(orgId, id, force = false) {
    if (!orgId) {
      throw new Error('AiBotService.publishData: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.publishData: id undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/publish`, {
      orgId,
      force,
    });

    return response || {};
  }

  static async deleteDataset(orgId, id, ids, dataType, isAll) {
    if (!orgId) {
      throw new Error('AiBotService.deleteDataset: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.deleteDataset: id undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/dataset/delete`, {
      ids,
      orgId,
      dataType,
      isAll,
    });

    return response || {};
  }

  static async addFile(id, formData) {
    if (!id) {
      throw new Error('AiBotService.addFile: id undefined');
    }

    const response = await baseAPI.post(
      `/ai-bot/${id}/dataset/add-file`,
      formData,
    );

    return response || {};
  }

  static async addUrl(orgId, id, url) {
    if (!orgId) {
      throw new Error('AiBotService.addUrl: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.addUrl: id undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/dataset/add-url`, {
      url,
      orgId,
    });

    return response || {};
  }

  static async simulate(orgId, id, question) {
    if (!orgId) {
      throw new Error('AiBotService.simulate: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.simulate: id undefined');
    }

    if (!question) {
      throw new Error('AiBotService.simulate: question undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/simulate`, {
      orgId,
      question,
    });

    return response || {};
  }

  static async embeddings(orgId, id, dataType) {
    if (!orgId) {
      throw new Error('AiBotService.embeddings: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.embeddings: id undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/dataset/embeddings`, {
      orgId,
      dataType,
    });

    return response || {};
  }

  static async getDatasetProgress(orgId, id) {
    if (!orgId) {
      throw new Error('AiBotService.getDatasetProgress: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getDatasetProgress: id undefined');
    }

    const response = await baseAPI.get(
      `/ai-bot/${id}/dataset/progress?orgId=${orgId}`,
    );

    return response || {};
  }

  static async getPoints(orgId) {
    if (!orgId) {
      throw new Error('AiBotService.getPoints: orgId undefined');
    }

    const response = await baseAPI.get(`/ai-bot/openai/point?orgId=${orgId}`);

    return response || {};
  }

  static async deleteTuning(orgId, id, datasetIds) {
    if (!orgId) {
      throw new Error('AiBotService.deleteTuning: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.deleteTuning: id undefined');
    }

    if (!datasetIds) {
      throw new Error('AiBotService.deleteTuning: datasetIds undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/tuning/delete`, {
      orgId,
      ids: datasetIds,
    });

    return response || {};
  }

  static async updateTuning(orgId, id, datasetId, data) {
    if (!id) {
      throw new Error('AiBotService.updateTuning: id undefined');
    }

    if (!datasetId) {
      throw new Error('AiBotService.updateTuning: datasetId undefined');
    }

    if (!orgId) {
      throw new Error('AiBotService.updateTuning: orgId undefined');
    }

    if (!data) {
      throw new Error('AiBotService.updateTuning: data undefined');
    }

    const response = await baseAPI.patch(`/ai-bot/${id}/tuning/${datasetId}`, {
      orgId,
      ...data,
    });

    return response || {};
  }

  static async addTuning(orgId, id, data) {
    if (!orgId) {
      throw new Error('AiBotService.addTuning: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.addTuning: id undefined');
    }

    if (!data) {
      throw new Error('AiBotService.addTuning: datasetId undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/tuning`, {
      orgId,
      ...data,
    });

    return response || {};
  }

  static async getTuning(orgId, id, skip = 0, limit = 20) {
    if (!orgId) {
      throw new Error('AiBotService.getTuning: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getTuning: id undefined');
    }

    const response = await baseAPI.get(
      `/ai-bot/${id}/tuning?orgId=${orgId}&skip=${skip}&limit=${limit}`,
    );

    return response || {};
  }

  static async refreshDataset(orgId, id, ids, dataType, isAll) {
    if (!orgId) {
      throw new Error('AiBotService.refreshDataset: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.refreshDataset: id undefined');
    }

    if (!ids) {
      throw new Error('AiBotService.refreshDataset: datasetIds undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/dataset/refresh/batch`, {
      orgId,
      ids,
      dataType,
      isAll,
    });

    return response || {};
  }

  static async getDatasetList(orgId, id, query, skip = 0, limit = 20) {
    if (!orgId) {
      throw new Error('AiBotService.getDatasetList: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getDatasetList: id undefined');
    }

    const response = await baseAPI.get(
      `/ai-bot/${id}/dataset/list?${qs.stringify({
        ...query,
        skip,
        limit,
        orgId,
        sort: query?.sort ? JSON.stringify(query?.sort) : undefined,
      })}`,
    );

    return response || {};
  }

  static async getDraftDatase(orgId, id, dataType = 'web') {
    if (!orgId) {
      throw new Error('AiBotService.getDatasetList: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getDatasetList: id undefined');
    }

    const response = await baseAPI.get(
      `/ai-bot/${id}/dataset/init/list?${qs.stringify({
        orgId,
        dataType,
      })}`,
    );

    return response || {};
  }

  static async getSiteList(orgId, id, url) {
    if (!orgId) {
      throw new Error('AiBotService.getDatasetList: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getDatasetList: id undefined');
    }

    const response = await baseAPI.post(`/ai-bot/${id}/dataset/site/list`, {
      url,
      orgId,
    });

    return response || {};
  }

  static async saveScheduledUpdatesSetting(orgId, id, data) {
    if (!orgId) {
      throw new Error(
        'AiBotService.saveScheduledUpdatesSetting: orgId undefined',
      );
    }

    if (!id) {
      throw new Error('AiBotService.saveScheduledUpdatesSetting: id undefined');
    }

    const response = await baseAPI.put(
      `/ai-bot/${id}/scheduled/setting/update`,
      {
        ...data,
        orgId,
      },
    );

    return response || {};
  }

  static async getDatasetListTotal(orgId, id, query) {
    if (!orgId) {
      throw new Error('AiBotService.getDatasetList: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getDatasetList: id undefined');
    }

    const response = await baseAPI.get(
      `/ai-bot/${id}/dataset/count?${qs.stringify({
        ...query,
        orgId,
        sort: undefined,
      })}`,
    );

    return response || {};
  }

  static async getUsed(orgId, id, dataType) {
    if (!orgId) {
      throw new Error('AiBotService.getUsed: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getUsed: id undefined');
    }

    const response = await baseAPI.post(
      `/ai-bot/${id}/dataset/used?orgId=${orgId}`,
      {
        dataType,
      },
    );

    return response?.used || 0;
  }

  static async getDatasetInitCount(orgId, id) {
    if (!orgId) {
      throw new Error('AiBotService.getDatasetInitCount: orgId undefined');
    }

    if (!id) {
      throw new Error('AiBotService.getDatasetInitCount: id undefined');
    }

    const response = await baseAPI.get(
      `/ai-bot/${id}/dataset/init/count?orgId=${orgId}`,
    );

    return response || {};
  }
}
